import {
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Typography,
  Divider,
  // Modal, // Removed if not used
  Box,
  // Select, // Removed if not used
  // MenuItem, // Removed if not used
  // FormControl as MuiFormControl, // Renamed to avoid conflict
  // InputLabel, // Removed if not used
  RadioGroup,
  FormControlLabel,
  Radio,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  Paper,
  CircularProgress, // Added for loading indication
  FormControl, // Keep this one for RadioGroup
  Alert, // Added for better feedback
} from "@mui/material";
import ArrowBackSharpIcon from "@mui/icons-material/ArrowBackSharp";
import React, { useEffect, useState, useCallback } from "react"; // Added useCallback
import axios from "axios";
import { useNavigate } from "react-router-dom";
import HOST_URL from "../../../utils/Url";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ToastApp from "../../../ToastApp"; // Assuming this component is correctly set up
import Autocomplete from "@mui/material/Autocomplete";
// import { Margin } from "@mui/icons-material"; // Removed if not used
// import nopdf from "../../../assets/images/nopdf.png"; // Removed if not used

// Removed unused style object

type DocType = {
  value: string;
  label: string;
};

type TableRowData = {
  id: number;
  fileNo: string;
  docMid: number;
  fileDef: string;
  isSelected: boolean;
};

// Define validation schema
const validationSchema = Yup.object({
  docID: Yup.number()
    .min(0, "Document Type is required") // Assuming -1 is the placeholder value
    .required("Document Type is required"),
  // No validation needed for the table data directly in Yup here,
  // but we'll check selection in onSubmit
});

const UpdatePDF: React.FC = () => { // Use React.FC for functional components
  const navigate = useNavigate();
  const { t } = useTranslation();

  // State variables
  const [tableData, setTableData] = useState<TableRowData[]>([]);
  const [docTypeOptions, setDocTypeOptions] = useState<DocType[]>([]);
  const [isLoadingDocTypes, setIsLoadingDocTypes] = useState<boolean>(false);
  const [isLoadingDocs, setIsLoadingDocs] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [selectedAction, setSelectedAction] = useState<string>("rename"); // 'rename' or 'shift'

  // --- Data Fetching ---

  const getDocType = useCallback(async () => {
    setIsLoadingDocTypes(true);
    const collectData = { docID: -1, parentDocId: -1, userId: "" };
    try {
      const response = await axios.post(
        HOST_URL.HOST_URL2 + `OtherDocType/GetOtherDocType`,
        collectData
      );
      if (response.data?.data) {
        const formattedOptions: DocType[] = response.data.data.map(
          (item: any) => ({
            label: item["doc_Name"],
            value: item["docID"].toString(), // Ensure value is string if needed by Autocomplete
          })
        );
        // Add the placeholder option if desired, or handle selection requirement differently
        // setDocTypeOptions([{ value: "-1", label: t("text.SelectDocType") }, ...formattedOptions]);
        setDocTypeOptions(formattedOptions); // Better to handle required selection via validation
      } else {
        console.error("Failed to fetch doc types or data format incorrect");
        toast.error("Failed Fetch DocTypes");
      }
    } catch (error) {
      console.error("Error fetching doc types:", error);
      toast.error("Failed Fetch DocTypes");
    } finally {
      setIsLoadingDocTypes(false);
    }
  }, [t]); // Add dependencies

  useEffect(() => {
    getDocType();
  }, [getDocType]); // useEffect depends on getDocType

  const fetchDocuments = async (docId: number) => {
    if (docId < 0) {
      setTableData([]); // Clear table if no valid doc type is selected
      return;
    }
    setIsLoadingDocs(true);
    setTableData([]); // Clear previous data
    const collectData = {
      docMid: -1,
      fileTypId: docId,
      divisionid: -1,
      subsubjId: -1,
      user_Id: -1, // Consider using actual user ID if available
      fileNo: "",
    };
    try {
      const response = await axios.post(
        HOST_URL.HOST_URL2 + `DocMangr/GetDocMangr`,
        collectData
      );
      if (response.data?.data) {
        const formattedData: TableRowData[] = response.data.data.map(
          (item: any, index: number) => ({
            // Use index or a unique ID from data if available for the 'id' field
            id: item["docMid"] || index, // Prefer docMid if always unique and present
            fileNo: item["fileNo"],
            docMid: item["docMid"],
            fileDef: item["fileDef"],
            isSelected: false,
          })
        );
        setTableData(formattedData);
      } else {
        setTableData([]); // Ensure table is empty on failed fetch or no data
        console.error("Failed to fetch documents or data format incorrect");
        // Optionally show a toast, but the empty table message might suffice
        // toast.warn(t("text.noDocumentsFound"));
      }
    } catch (error) {
      setTableData([]);
      console.error("Error fetching documents:", error);
      toast.error("Fetch Documents");
    } finally {
      setIsLoadingDocs(false);
    }
  };

  // --- Form Handling ---

  const formik = useFormik({
    initialValues: {
      docID: -1, // Use number consistent with validation
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);

      const selectedFiles = tableData.filter((row) => row.isSelected);

      if (selectedFiles.length === 0) {
        toast.warn("no file slected");
        setIsSubmitting(false);
        return;
      }

      // Prepare payload based on the selected action
      let payload;
      let apiUrl;

      if (selectedAction === "rename") {
        payload = {
          udatePdfFiles: selectedFiles.map((row) => ({
            docID: row.docMid, // Assuming docMid is the ID to use for update
            doc_Name: row.fileDef, // The potentially updated file name
          })),
          // Add any other required fields based on API spec
          // e.g., fileTypId: values.docID
        };
        apiUrl = HOST_URL.HOST_URL2 + `DocMangr/UpdatePdfFileName`;
      } else if (selectedAction === "shift") {
        // --- TODO: Implement Shift Category Logic ---
        // 1. Add a new Autocomplete/Select for the target category.
        // 2. Update Yup validation if needed.
        // 3. Prepare the payload for the shift API endpoint.
        // payload = { ... };
        // apiUrl = HOST_URL.HOST_URL2 + `YourShiftApiEndpoint`;
        toast.info("Shift category functionality not yet implemented.");
        setIsSubmitting(false);
        return; // Prevent submission until implemented
      } else {
        toast.error("Invalid action selected.");
        setIsSubmitting(false);
        return;
      }


      try {
        const response = await axios.post(apiUrl, payload);
        if (response.data.isSuccess) {
          toast.success(response.data.mesg);
          // Reset selection and potentially refetch or clear table
          setTableData((prev) =>
            prev.map((row) => ({ ...row, isSelected: false }))
          );
          // Optionally clear the table or refetch if the underlying data might change significantly
          // setTableData([]);
          // formik.resetForm(); // Reset doc type selection as well? Decide based on workflow.
          // Or just refetch for the current doc type:
          fetchDocuments(values.docID); // Refetch to show updated names (if API returns them) or just confirm success
        } else {
          toast.error(response.data.mesg);
        }
      } catch (error) {
        console.error("Error submitting update:", error);
        toast.error("Update Failed Generic");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  // --- Event Handlers ---

  const handleInputChange = (id: number, field: keyof TableRowData, value: any) => {
    setTableData((prevTableData) =>
      prevTableData.map((row) =>
        row.id === id ? { ...row, [field]: value } : row
      )
    );
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setTableData((prevTableData) =>
      prevTableData.map((row) => ({ ...row, isSelected: isChecked }))
    );
  };

  const handleActionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedAction((event.target as HTMLInputElement).value);
    // Reset selection when action changes? Optional.
    // setTableData(prev => prev.map(row => ({ ...row, isSelected: false })));
  };

  // Calculate if all rows are selected (for the header checkbox)
  const numSelected = tableData.filter((row) => row.isSelected).length;
  const rowCount = tableData.length;
  const isAllSelected = rowCount > 0 && numSelected === rowCount;

  // --- Render ---

  return (
    <Box sx={{ p: 3, backgroundColor: 'background.default', minHeight: 'calc(100vh - 64px)' }}> {/* Adjust minHeight based on AppBar height */}
      <ToastApp />
      <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            bgcolor: 'primary.main', // Or use theme color
            color: 'primary.contrastText', // Or use theme color
            // backgroundColor: 'var(--header-background)', // Use CSS var if defined globally
            // color: 'var(--header-color)', // Use CSS var if defined globally
          }}
        >
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            {t("text.UpdatePdf")}
          </Typography>
          <Button
            variant="outlined" // Changed for better contrast on colored background
            size="small"
            onClick={() => navigate(-1)}
            startIcon={<ArrowBackSharpIcon />}
            sx={{
              borderColor: 'primary.contrastText',
              color: 'primary.contrastText',
              '&:hover': {
                bgcolor: 'primary.dark', // Adjust hover effect
                borderColor: 'primary.contrastText',
              }
            }}
          >
            {t("text.Back")}
          </Button>
        </Box>

        <CardContent sx={{ p: 3 }}>
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={3}>
              {/* --- Controls Section --- */}
              <Grid item xs={12} md={4}>
                <Autocomplete
                  id="doc-type-autocomplete"
                  options={docTypeOptions}
                  getOptionLabel={(option) => option.label}
                  value={
                    docTypeOptions.find(
                      (option) => option.value === formik.values.docID.toString()
                    ) || null
                  }
                  onChange={(_, newValue) => {
                    const newId = newValue ? parseInt(newValue.value) : -1;
                    formik.setFieldValue("docID", newId);
                    // Automatically fetch documents when a type is selected
                    // Or keep the separate search button if preferred
                    if (newId !== -1) {
                      fetchDocuments(newId); // Fetch immediately
                    } else {
                      setTableData([]); // Clear table if selection is cleared
                    }
                  }}
                  loading={isLoadingDocTypes}
                  disabled={isLoadingDocTypes || isSubmitting || isLoadingDocs}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("text.SelectDocType")}
                      error={formik.touched.docID && Boolean(formik.errors.docID)}
                      helperText={formik.touched.docID && formik.errors.docID}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isLoadingDocTypes ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={5}>
                <FormControl component="fieldset" disabled={isSubmitting || isLoadingDocs}>
                  {/* <FormLabel component="legend">{t("text.SelectAction")}</FormLabel> */}
                  <RadioGroup
                    row
                    aria-label="update-action"
                    name="action-radio-group"
                    value={selectedAction}
                    onChange={handleActionChange}
                  >
                    <FormControlLabel
                      value="rename" // Use simple value
                      control={<Radio />}
                      label={t("text.ForRename")} // Keep using translation key
                    />
                    <FormControlLabel
                      value="shift" // Use simple value
                      control={<Radio />}
                      label={t("text.ForShiftCategory")} // Keep using translation key
                    // disabled // Disable until implemented
                    />
                    {/* Add more actions here if needed */}
                  </RadioGroup>
                </FormControl>
              </Grid>

              {/* Keep Search Button if explicit trigger is desired */}
              {/* <Grid item xs={12} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
                <Button
                  onClick={() => fetchDocuments(formik.values.docID)}
                  variant="contained"
                  fullWidth
                  disabled={formik.values.docID < 0 || isLoadingDocs || isSubmitting || isLoadingDocTypes}
                  startIcon={isLoadingDocs ? <CircularProgress size={20} color="inherit" /> : null}
                  sx={{ height: '56px' }} // Match TextField height
                >
                  {isLoadingDocs ? t("text.loading") : t("text.search")}
                </Button>
              </Grid> */}

              {/* --- Table Section --- */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                {isLoadingDocs ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
                    <CircularProgress />
                  </Box>
                ) : tableData.length === 0 && formik.values.docID !== -1 ? (
                  <Alert severity="info" sx={{ mt: 2 }}>{t("text.noDocumentsFoundForType")}</Alert>
                ) : tableData.length === 0 && formik.values.docID === -1 ? (
                  <Alert severity="info" sx={{ mt: 2 }}>{t("text.selectTypeToViewDocs")}</Alert>
                ) : (
                  <TableContainer component={Paper} elevation={1} sx={{ maxHeight: '50vh' }}> {/* Added max height and scroll */}
                    <Table stickyHeader aria-label="document update table">
                      <TableHead>
                        <TableRow sx={{ '& th': { backgroundColor: 'grey.200', fontWeight: 'bold' } }}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              color="primary"
                              indeterminate={numSelected > 0 && numSelected < rowCount}
                              checked={isAllSelected}
                              onChange={handleSelectAll}
                              inputProps={{ 'aria-label': 'select all documents' }}
                              disabled={isSubmitting || isLoadingDocs}
                            />
                          </TableCell>
                          <TableCell>{t("text.FileNo")}</TableCell>
                          <TableCell>{t("text.PdfFileName")}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {tableData.map((row) => (
                          <TableRow
                            hover
                            key={row.id} // Use a unique key
                            role="checkbox"
                            aria-checked={row.isSelected}
                            selected={row.isSelected}
                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                color="primary"
                                checked={row.isSelected}
                                onChange={(e) => handleInputChange(row.id, "isSelected", e.target.checked)}
                                inputProps={{ 'aria-labelledby': `checkbox-label-${row.id}` }}
                                disabled={isSubmitting || isLoadingDocs}
                              />
                            </TableCell>
                            <TableCell component="th" id={`checkbox-label-${row.id}`} scope="row">
                              {row.fileNo}
                            </TableCell>
                            <TableCell>
                              <TextField
                                variant="outlined" // Or 'standard' for less visual clutter
                                size="small"
                                fullWidth
                                type="text"
                                value={row.fileDef}
                                onChange={(e) => handleInputChange(row.id, "fileDef", e.target.value)}
                                disabled={!row.isSelected || selectedAction !== 'rename' || isSubmitting || isLoadingDocs} // Only enable if selected and action is rename
                                sx={{
                                  backgroundColor: row.isSelected && selectedAction === 'rename' ? 'white' : 'grey.100', // Visual cue
                                  minWidth: '250px' // Ensure enough space
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Grid>

              {/* --- Action Buttons Section --- */}
              {tableData.length > 0 && ( // Only show buttons if there's data
                <Grid item xs={12} container spacing={2} justifyContent="center" sx={{ mt: 2 }}>
                  <Grid item xs={12} sm={4} md={3}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      fullWidth
                      disabled={isSubmitting || isLoadingDocs || numSelected === 0}
                      startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                      {isSubmitting ? t("text.saving") : t("text.save")}
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={4} md={3}>
                    <Button
                      type="reset" // Resets formik state, not table selection easily
                      variant="outlined"
                      color="secondary" // Use theme color
                      fullWidth
                      disabled={isSubmitting || isLoadingDocs}
                      onClick={() => {
                        formik.resetForm(); // Reset doc type selection
                        setTableData([]); // Clear table
                        setSelectedAction('rename'); // Reset action
                      }}
                    // sx={{ backgroundColor: '#F43F5E', color: 'white' }} // Avoid hardcoding if possible
                    >
                      {t("text.reset")}
                    </Button>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </form>
        </CardContent>
      </Paper>
    </Box>
  );
};

export default UpdatePDF;
